import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useTheme } from '../../context/ThemeContext';
import CardImage from '../../components/CardImage';

// Icons
const ChevronLeftIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m15 18-6-6 6-6" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m9 18 6-6-6-6" />
  </svg>
);

const ClockIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const XIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 6L6 18" />
    <path d="M6 6l12 12" />
  </svg>
);

const CalendarIcon = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const AlertIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
  </svg>
);

const LocationTrades = ({ location }) => {
  const { theme } = useTheme();

  // Trades/Schedule state
  const [upcomingTrades, setUpcomingTrades] = useState([]);
  const [tradesLoading, setTradesLoading] = useState(false);
  const [selectedTrade, setSelectedTrade] = useState(null);

  // Week navigation
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day;
    return new Date(today.setDate(diff));
  });

  // Fetch trades
  useEffect(() => {
    if (location?.id) {
      fetchUpcomingTrades();
    }
  }, [location?.id]);

  const fetchUpcomingTrades = async () => {
    if (!location?.id) return;

    setTradesLoading(true);
    try {
      const { data, error } = await supabase
        .from('trade_schedules')
        .select(`
          id,
          selected_date,
          selected_time,
          status,
          location_id,
          trade_requests (
            id,
            requester:users!trade_requests_requester_id_fkey (
              username
            ),
            card_owner:users!trade_requests_card_owner_id_fkey (
              username
            ),
            cards (
              name,
              digital_image_url,
              original_image_url
            )
          )
        `)
        .eq('location_id', location.id)
        .eq('status', 'confirmed')
        .order('created_at', { ascending: false })
        .limit(50);

      if (!error && data) {
        setUpcomingTrades(data);
      }
    } catch (err) {
      console.error('[LocationTrades] Error fetching trades:', err);
    } finally {
      setTradesLoading(false);
    }
  };

  // Week helpers
  const getWeekDays = () => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(currentWeekStart);
      date.setDate(currentWeekStart.getDate() + i);
      days.push(date);
    }
    return days;
  };

  const formatDateKey = (date) => `${date.getMonth() + 1}/${date.getDate()}`;

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const navigateWeek = (direction) => {
    const newStart = new Date(currentWeekStart);
    newStart.setDate(currentWeekStart.getDate() + (direction * 7));
    setCurrentWeekStart(newStart);
  };

  const goToToday = () => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day;
    setCurrentWeekStart(new Date(today.setDate(diff)));
  };

  const getTradesForDate = (dateKey) => {
    return upcomingTrades
      .filter(t => t.selected_date === dateKey)
      .sort((a, b) => (a.selected_time || '').localeCompare(b.selected_time || ''));
  };

  const groupTradesByTime = (dayTrades) => {
    const grouped = {};
    dayTrades.forEach(trade => {
      const time = trade.selected_time || 'TBD';
      if (!grouped[time]) grouped[time] = [];
      grouped[time].push(trade);
    });
    return grouped;
  };

  const weekDays = getWeekDays();
  const weekRange = `${weekDays[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${weekDays[6].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;

  // Count total trades this week
  const totalTradesThisWeek = weekDays.reduce((sum, day) => {
    return sum + getTradesForDate(formatDateKey(day)).length;
  }, 0);

  // Get the next upcoming trade for today
  const getNextUpTrade = () => {
    const today = new Date();
    const todayKey = formatDateKey(today);
    const todayTrades = getTradesForDate(todayKey);
    if (!todayTrades.length) return null;

    const nowMinutes = today.getHours() * 60 + today.getMinutes();

    let closest = null;
    let closestMinutes = Infinity;

    for (const trade of todayTrades) {
      const timeStr = trade.selected_time;
      if (!timeStr) continue;

      let tradeMinutes = 0;
      const match12 = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
      const match24 = timeStr.match(/^(\d{1,2}):(\d{2})$/);

      if (match12) {
        let hours = parseInt(match12[1]);
        const mins = parseInt(match12[2]);
        const period = match12[3].toUpperCase();
        if (period === 'PM' && hours !== 12) hours += 12;
        if (period === 'AM' && hours === 12) hours = 0;
        tradeMinutes = hours * 60 + mins;
      } else if (match24) {
        tradeMinutes = parseInt(match24[1]) * 60 + parseInt(match24[2]);
      } else {
        continue;
      }

      if (tradeMinutes >= nowMinutes && tradeMinutes < closestMinutes) {
        closest = trade;
        closestMinutes = tradeMinutes;
      }
    }

    return closest || todayTrades[todayTrades.length - 1];
  };

  const nextUpTrade = getNextUpTrade();

  return (
    <div style={styles.container}>
      {/* Header Stats */}
      <div style={{ ...styles.statsCard, backgroundColor: theme.bgSheet, borderColor: theme.border }}>
        <div style={{ ...styles.statsIcon, backgroundColor: 'rgba(139, 92, 246, 0.15)' }}>
          <CalendarIcon />
        </div>
        <div style={styles.statsInfo}>
          <span style={{ ...styles.statsNumber, color: '#8b5cf6' }}>{totalTradesThisWeek}</span>
          <span style={{ ...styles.statsLabel, color: theme.textSecondary }}>Trades This Week</span>
        </div>
      </div>

      {/* Next Up Card */}
      {nextUpTrade ? (
        <div
          style={{
            ...styles.nextUpCard,
            backgroundColor: theme.bgSheet,
            borderColor: '#8b5cf6',
          }}
          onClick={() => setSelectedTrade(nextUpTrade)}
        >
          <div style={styles.nextUpHeader}>
            <div style={styles.nextUpBadge}>
              <AlertIcon />
              <span style={styles.nextUpBadgeText}>Next Up</span>
            </div>
            <span style={{ ...styles.nextUpTime, color: theme.text }}>
              {nextUpTrade.selected_time || 'TBD'}
            </span>
          </div>
          <div style={styles.nextUpBody}>
            <span style={{ ...styles.nextUpUsers, color: theme.text }}>
              @{nextUpTrade.trade_requests?.requester?.username || 'user'} & @{nextUpTrade.trade_requests?.card_owner?.username || 'user'}
            </span>
            {nextUpTrade.trade_requests?.cards?.name && (
              <span style={{ ...styles.nextUpCardName, color: theme.textSecondary }}>
                {nextUpTrade.trade_requests.cards.name}
              </span>
            )}
          </div>
        </div>
      ) : (
        <div style={{
          ...styles.nextUpCard,
          backgroundColor: theme.bgSheet,
          borderColor: theme.border,
          opacity: 0.6,
        }}>
          <div style={styles.nextUpHeader}>
            <div style={{ ...styles.nextUpBadge, backgroundColor: 'rgba(107, 114, 128, 0.15)' }}>
              <AlertIcon />
              <span style={{ ...styles.nextUpBadgeText, color: '#6b7280' }}>Next Up</span>
            </div>
          </div>
          <span style={{ ...styles.nextUpCardName, color: theme.textSecondary }}>No trades today</span>
        </div>
      )}

      {/* Week Navigation */}
      <div style={{ ...styles.weekNav, backgroundColor: theme.bgSheet, borderColor: theme.border }}>
        <button onClick={() => navigateWeek(-1)} style={{ ...styles.weekNavBtn, backgroundColor: theme.bg, borderColor: theme.border, color: theme.text }}>
          <ChevronLeftIcon />
        </button>
        <div style={styles.weekInfo}>
          <span style={{ ...styles.weekRange, color: theme.text }}>{weekRange}</span>
          <button onClick={goToToday} style={styles.todayBtn}>Today</button>
        </div>
        <button onClick={() => navigateWeek(1)} style={{ ...styles.weekNavBtn, backgroundColor: theme.bg, borderColor: theme.border, color: theme.text }}>
          <ChevronRightIcon />
        </button>
      </div>

      {tradesLoading ? (
        <div style={styles.loadingTrades}>
          <div style={styles.smallSpinner} />
          <span style={{ color: theme.textSecondary }}>Loading trades...</span>
        </div>
      ) : (
        <div style={styles.agendaView}>
          {weekDays.map(day => {
            const dateKey = formatDateKey(day);
            const dayTrades = getTradesForDate(dateKey);
            const timeGroups = groupTradesByTime(dayTrades);
            const dayIsToday = isToday(day);

            return (
              <div key={dateKey} style={{ ...styles.agendaDay, backgroundColor: theme.bgSheet, borderColor: theme.border }}>
                <div style={{
                  ...styles.agendaDayHeader,
                  backgroundColor: dayIsToday ? 'rgba(139, 92, 246, 0.1)' : theme.bg,
                  borderColor: theme.border,
                }}>
                  <span style={{ ...styles.agendaDayName, color: theme.textSecondary }}>
                    {day.toLocaleDateString('en-US', { weekday: 'short' })}
                  </span>
                  <span style={{
                    ...styles.agendaDayDate,
                    color: dayIsToday ? '#8b5cf6' : theme.text,
                  }}>
                    {day.getDate()}
                  </span>
                  {dayIsToday && <span style={styles.todayLabel}>Today</span>}
                  {dayTrades.length > 0 && (
                    <span style={styles.tradeCount}>{dayTrades.length}</span>
                  )}
                </div>
                <div style={styles.agendaDayContent}>
                  {dayTrades.length === 0 ? (
                    <div style={{ ...styles.noTrades, color: theme.textSecondary }}>No trades scheduled</div>
                  ) : (
                    Object.entries(timeGroups).map(([time, trades]) => (
                      <div key={time} style={styles.timeGroup}>
                        <div style={styles.timeLabel}>{time}</div>
                        {trades.map(trade => {
                          const request = trade.trade_requests;
                          return (
                            <div
                              key={trade.id}
                              style={{ ...styles.scheduleTradeCard, backgroundColor: theme.bg, borderColor: theme.border }}
                              onClick={() => setSelectedTrade(trade)}
                            >
                              <span style={{ ...styles.scheduleTradeUsers, color: theme.text }}>
                                @{request?.requester?.username || 'user'} & @{request?.card_owner?.username || 'user'}
                              </span>
                              {request?.cards?.name && (
                                <span style={{ ...styles.scheduleTradeCard, color: theme.textSecondary, fontSize: 11 }}>
                                  {request.cards.name}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Trade Detail Modal */}
      {selectedTrade && (
        <>
          <div style={styles.modalOverlay} onClick={() => setSelectedTrade(null)} />
          <div style={{ ...styles.modal, backgroundColor: theme.bgSheet }}>
            <div style={{ ...styles.modalHeader, borderColor: theme.border }}>
              <h3 style={{ ...styles.modalTitle, color: theme.text }}>Trade Details</h3>
              <button style={{ ...styles.modalClose, color: theme.textSecondary }} onClick={() => setSelectedTrade(null)}>
                <XIcon />
              </button>
            </div>

            <div style={styles.tradeDetailContent}>
              {selectedTrade.trade_requests?.cards && (
                <CardImage
                  digitalImage={selectedTrade.trade_requests.cards.digital_image_url}
                  userPhoto={selectedTrade.trade_requests.cards.original_image_url}
                  name={selectedTrade.trade_requests.cards.name}
                  theme={theme}
                  style={styles.tradeDetailImage}
                />
              )}
              <div style={styles.tradeDetailInfo}>
                <div style={{ ...styles.tradeDetailRow, color: theme.text }}>
                  <ClockIcon />
                  <span>{selectedTrade.selected_date} at {selectedTrade.selected_time}</span>
                </div>
                <div style={{ ...styles.tradeDetailRow, color: theme.text }}>
                  <span style={{ color: theme.textSecondary }}>Traders:</span>
                  <span>
                    @{selectedTrade.trade_requests?.requester?.username || 'user'} & @{selectedTrade.trade_requests?.card_owner?.username || 'user'}
                  </span>
                </div>
                {selectedTrade.trade_requests?.cards?.name && (
                  <div style={{ ...styles.tradeDetailRow, color: theme.text }}>
                    <span style={{ color: theme.textSecondary }}>Card:</span>
                    <span>{selectedTrade.trade_requests.cards.name}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const styles = {
  container: {
    padding: 16,
    paddingBottom: 120,
  },
  statsCard: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    padding: 20,
    borderRadius: 16,
    border: '1px solid',
    marginBottom: 16,
  },
  statsIcon: {
    width: 64,
    height: 64,
    borderRadius: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#8b5cf6',
  },
  statsInfo: {
    display: 'flex',
    flexDirection: 'column',
  },
  statsNumber: {
    fontSize: 36,
    fontWeight: 800,
  },
  statsLabel: {
    fontSize: 14,
  },
  nextUpCard: {
    padding: 16,
    borderRadius: 12,
    border: '2px solid',
    marginBottom: 16,
    cursor: 'pointer',
  },
  nextUpHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  nextUpBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    backgroundColor: 'rgba(139, 92, 246, 0.15)',
    padding: '4px 10px',
    borderRadius: 6,
    color: '#8b5cf6',
  },
  nextUpBadgeText: {
    fontSize: 12,
    fontWeight: 700,
    color: '#8b5cf6',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  nextUpTime: {
    fontSize: 16,
    fontWeight: 700,
  },
  nextUpBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  nextUpUsers: {
    fontSize: 15,
    fontWeight: 600,
  },
  nextUpCardName: {
    fontSize: 12,
  },
  loadingTrades: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: 32,
  },
  smallSpinner: {
    width: 16,
    height: 16,
    border: '2px solid var(--border)',
    borderTopColor: '#8b5cf6',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  weekNav: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 12,
    borderRadius: 10,
    marginBottom: 12,
    border: '1px solid',
  },
  weekNavBtn: {
    width: 36,
    height: 36,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid',
    borderRadius: 8,
    cursor: 'pointer',
  },
  weekInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  weekRange: {
    fontSize: 14,
    fontWeight: 600,
  },
  todayBtn: {
    padding: '5px 10px',
    backgroundColor: '#8b5cf6',
    border: 'none',
    borderRadius: 6,
    color: '#fff',
    fontSize: 11,
    fontWeight: 600,
    cursor: 'pointer',
  },
  agendaView: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  agendaDay: {
    borderRadius: 8,
    overflow: 'hidden',
    border: '1px solid',
  },
  agendaDayHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 12px',
    borderBottom: '1px solid',
  },
  agendaDayName: {
    fontSize: 12,
  },
  agendaDayDate: {
    fontSize: 15,
    fontWeight: 600,
  },
  todayLabel: {
    fontSize: 10,
    fontWeight: 600,
    padding: '2px 6px',
    borderRadius: 4,
    backgroundColor: 'rgba(139, 92, 246, 0.15)',
    color: '#8b5cf6',
    marginLeft: 'auto',
  },
  tradeCount: {
    fontSize: 10,
    fontWeight: 700,
    padding: '2px 8px',
    borderRadius: 10,
    backgroundColor: '#8b5cf6',
    color: '#fff',
  },
  agendaDayContent: {
    padding: '8px 12px',
    minHeight: 40,
  },
  noTrades: {
    fontSize: 12,
    textAlign: 'center',
    padding: 8,
  },
  timeGroup: {
    marginBottom: 8,
  },
  timeLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: '#8b5cf6',
    marginBottom: 4,
  },
  scheduleTradeCard: {
    padding: '8px 10px',
    border: '1px solid',
    borderRadius: 6,
    marginBottom: 4,
    cursor: 'pointer',
  },
  scheduleTradeUsers: {
    fontSize: 12,
  },
  // Modal styles
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 1000,
  },
  modal: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    padding: 16,
    paddingBottom: 'calc(16px + env(safe-area-inset-bottom))',
    maxHeight: '70vh',
    overflowY: 'auto',
    zIndex: 1001,
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
    paddingBottom: 16,
    borderBottom: '1px solid',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 600,
    margin: 0,
  },
  modalClose: {
    padding: 8,
    margin: -8,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
  },
  tradeDetailContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  tradeDetailImage: {
    width: '100%',
    maxWidth: 150,
    borderRadius: 8,
    alignSelf: 'center',
  },
  tradeDetailInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  tradeDetailRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 14,
  },
};

export default LocationTrades;
